// Who builds what the user asks for. With a key saved for the chat beside the
// board, Claude (Anthropic's key) or Codex (OpenAI's) does. Without one, but
// with Claude Code (or any agent) connected, the request is copied for that
// agent to paste. With neither, the chat opens on a choice that leads with the
// AI app the user already pays for, and the request waits. Every "build it",
// "change it" and "fix it" in the app goes through here, so they all behave
// the same way.

import { runnerReady } from "./runnerChat";
import { agentKey, isBoardChat } from "../../shared/authors";
import type { ApiKey, RunnerState } from "../../shared/types";
import { copyText } from "../lib/util";
import { get, toast, useStore } from "../state/store";
import { canLeave, chat, openChat, switchAgent, useChat } from "./store";
import { chatName, type Provider } from "./config";

export interface Ask {
  /** what the chat beside the board is asked */
  chat: string;
  /** the same, worded for an agent outside the board */
  agent: string;
  /** the user finishes it: the chat puts it in the box, the agent's copy says to */
  finish?: boolean;
}

export type Builder = { mode: "chat" | "agent" | "none"; agent: string | null; /** who answers in the chat here: Claude, or Codex */ chat: string };

/**
 * The agent connected to this board that was used last, by the name the user knows it by. The keys the
 * runner's own agents use (the board's chat, signing as "Claude (chat)", and its briefs) are the board's, not
 * the user's agent, so they never name it.
 */
export function agentName(keys: ApiKey[]): string | null {
  const agents = keys.filter((k) => k.kind === "agent" && k.via !== "runner" && !isBoardChat(k.label));
  if (!agents.length) return null;
  // by its app's name alone: "Claude Code on my-pc" reads "Claude Code", the computer's name is not for buttons
  return agentKey([...agents].sort((a, b) => (b.lastUsedAt ?? b.createdAt) - (a.lastUsedAt ?? a.createdAt))[0].label);
}

/**
 * Who the user's requests go to, as buttons name them: Claude when the chat
 * here has a key, else the agent they connected, by its own name, else "your
 * agent". Not everyone uses Claude, so nothing says Claude unless it is.
 */
export function askWho(b: Builder): string {
  return b.mode === "chat" ? b.chat : (b.agent ?? "your agent");
}

/**
 * Who makes a theme: Codex or Claude, in the chat beside the board. Each runs on
 * the user's own plan when their computer runs it, else on their API key, else
 * the chat shows how to set it up, with the request waiting (David, 12 Oct 2026).
 */
export interface Maker {
  id: string;
  name: string;
  mode: "chat";
  provider: Provider;
  /** it makes the picture itself, where Claude finds a photograph */
  pictures: boolean;
  /** how it runs right now: on the user's own plan, on their API key, or not yet */
  how: "plan" | "key" | null;
}

/** Codex first, since it makes the picture itself; then Claude, which finds one online. */
export function themeMakers(config: { provider: Provider; key: string | null; otherKey: boolean }, runner: RunnerState | null | undefined): Maker[] {
  const how = (p: Provider): Maker["how"] => (runnerReady(runner, p) ? "plan" : (config.provider === p ? config.key : config.otherKey) ? "key" : null);
  return [
    { id: "chat:openai", name: chatName("openai"), mode: "chat", provider: "openai", pictures: true, how: how("openai") },
    { id: "chat:anthropic", name: chatName("anthropic"), mode: "chat", provider: "anthropic", pictures: false, how: how("anthropic") },
  ];
}

/** The same, live, so the words change the moment a key or the user's computer comes or goes. */
export function useThemeMakers(): Maker[] {
  const config = useChat((s) => s.config);
  const runner = useStore((s) => s.runner);
  return themeMakers(config, runner);
}

/**
 * A request to the one the user chose: that one's own chat, switched to first
 * as the chat's own switch does. False when the chat is busy and cannot switch.
 */
export function handOffTo(m: Maker, ask: Ask): boolean {
  const now = chat.get();
  if (m.provider && m.provider !== now.config.provider) {
    // a reply on the computer goes on in the chat left; one on an API key runs in this page and holds it
    if (!canLeave(now)) return false;
    switchAgent(m.provider);
  }
  openChat(ask.chat, ask.finish ? { send: false } : {});
  return true;
}

/** "Ask Codex", "Ask Claude", "Ask your agent". */
export function askLabel(b: Builder): string {
  return `Ask ${askWho(b)}`;
}

/**
 * The one rule for who answers: the chat here when it can run (a key, or the user's own computer), else the agent
 * connected, else no one yet. The chat is Claude or Codex by name, on a key or on the user's own computer alike:
 * "Ask Claude", never "Ask Claude Code" (David, 13 Oct). An agent connected from outside keeps its own name.
 */
function builderOf(canChat: boolean, agent: string | null, provider: Provider): Builder {
  return { mode: canChat ? "chat" : agent ? "agent" : "none", agent, chat: chatName(provider) };
}

/** The same, outside React: for menus built on the spot, and for handOff itself. */
export function builderNow(): Builder {
  const { config } = chat.get();
  const onRunner = runnerReady(get().runner, config.provider);
  return builderOf(!!config.key || onRunner, agentName(get().keys), config.provider);
}

/** Live, so words that name who answers change the moment a key or an agent comes or goes. */
export function useBuilder(): Builder {
  const hasKey = useChat((s) => !!s.config.key);
  const provider = useChat((s) => s.config.provider);
  const agent = useStore((s) => agentName(s.keys));
  const onRunner = useStore((s) => runnerReady(s.runner, provider));
  return builderOf(hasKey || onRunner, agent, provider);
}

/** Copies a request for the user's own agent, and says what to do with it. */
export function copyForAgent(text: string, agent: string | null, finish = false) {
  const who = agent ?? "your agent";
  void copyText(text).then(
    (ok) =>
      ok &&
      toast({
        level: "success",
        title: `Copied for ${who}`,
        body: finish ? `Paste it into ${who} and say what to change. This board changes as it works.` : `Paste it into ${who}. This board changes as it works.`,
      }),
  );
}

/** Give the request to whoever answers it, decided as useBuilder decides it, so a button's words and its click agree. */
export function handOff(ask: Ask) {
  const b = builderNow();
  if (b.mode === "chat") return openChat(ask.chat, ask.finish ? { send: false } : {});
  if (b.mode === "agent") return copyForAgent(ask.agent, b.agent, ask.finish);
  // neither yet: the chat opens on the choice, holding the request
  openChat(ask.chat, { send: !ask.finish, agent: ask.agent });
}
